import {
  GeolocationInvalidatedEvent as GeoInvalidatedEvent,
  GeolocationPassed as GeoPassedEvent,
  GeolocationRejected as GeoRejectedEvent,
  LocationFailure as GeoFailureEvent,
} from '@fanduel/geolocation-sdk';

import { isIosWrapper } from '@/features/game-context/platform-helpers';
import { type ProviderType } from '@/features/game-context/types';
import { logWBLaunchV2Info } from '@/features/logging/action-logger';
import { DdEventCategory } from '@/features/logging/logger';
import {
  logProviderMessages,
  MessageEventsLoggerOptions,
  providerMessagesInclusion,
} from '@/features/logging/message-event-logger';
import { iosBridgeMapper } from '@/features/wrapper-proxy/ios-bridge-mapper';
import {
  CLIENT_TO_WRAPPER_EVENT_TYPES,
  FetchFgpGameError,
  ProxyMessage,
  SetErrorPayload,
} from '@/features/wrapper-proxy/proxy-message';
import {
  getLauncherPostMessageTarget,
  resolveLauncherPostMessageOrigin,
} from '@/features/wrapper-proxy/resolve-launcher-message-origin';

export class WrapperProxy {
  private static sendWebWrapperMessage(payload: ProxyMessage) {
    const origin = resolveLauncherPostMessageOrigin();

    getLauncherPostMessageTarget()?.postMessage(payload, origin);
  }

  private static sendIosWrapperMessage(payload: ProxyMessage) {
    const handler = iosBridgeMapper(payload);
    try {
      if (handler) handler(payload);
    } catch (e) {
      logWBLaunchV2Info(`Send ios message ERROR:, ${e}`);
    }
  }

  private static sendMessage(
    payload: ProxyMessage,
    loggerOptions?: Partial<MessageEventsLoggerOptions>,
  ) {
    // Log certain wrapper messages after emitting them to the wrapper
    logProviderMessages(payload, {
      logType: loggerOptions?.logType ?? 'info',
      useCase: loggerOptions?.useCase ?? DdEventCategory.ProviderMessages,
      name: loggerOptions?.name ?? 'PROVIDER_MESSAGE',
      inclusions: providerMessagesInclusion,
    });

    if (this.isIosWrapper()) {
      // GO directly to iOS
      logWBLaunchV2Info('[WEB_LAUNCHER_V2]:Going directly to Ios..');
      this.sendIosWrapperMessage(payload);
    } else {
      // GO to launcher
      logWBLaunchV2Info('[WEB_LAUNCHER_V2]: Going through launcher..');
      this.sendWebWrapperMessage(payload);
    }
  }

  static isIosWrapper() {
    const isLauncherRefferer = document.referrer?.includes('launcher');
    return isIosWrapper() && !isLauncherRefferer;
  }

  static setGameClientInitialized() {
    this.sendMessage({ type: 'GC/INITIALIZE_HANDSHAKE' });
  }

  static setAudio(payload: boolean) {
    this.sendMessage({ type: 'GC/SET_AUDIO_ENABLED', payload });
  }

  static setGameLoaded() {
    this.sendMessage({ type: 'GC/SET_GAME_LOADED' });
  }

  static setSpinInProgress(payload: boolean) {
    this.sendMessage({ type: 'GC/SET_SPIN_IN_PROGRESS', payload });
  }

  static setProviderError(payload: SetErrorPayload) {
    this.sendMessage(
      { type: 'GC/SET_PROVIDER_ERROR', payload },
      {
        logType: 'error',
        useCase: DdEventCategory.ProviderMessages,
        name: 'PROVIDER_ERROR',
      },
    );
  }

  static setGameLoadProgress(payload: number) {
    this.sendMessage({ type: 'GC/SET_GAME_LOAD_PROGRESS', payload });
  }

  static setGameLoadStarted() {
    this.sendMessage({ type: 'GC/SET_GAMELOAD_STARTED' });
  }

  static showFunds() {
    this.sendMessage({ type: 'GC/SHOW_FUNDS' });
  }

  static resetGame() {
    this.sendMessage({ type: 'GC/RESET_GAME' });
  }

  static setGameLauncherClosed() {
    this.sendMessage({ type: 'GC/CLOSE_GAME_LAUNCHER' });
  }

  static setFetchGameFailure(payload?: FetchFgpGameError) {
    const gameError = payload ?? { errorCode: 'GENERIC_ERROR' };

    this.sendMessage({
      type: 'GC/FETCH_GAME_FAILURE',
      payload: gameError,
    });
  }

  static setContextParamsMissing(payload: string) {
    this.sendMessage({
      type: 'GC/CONTEXT_PARAM_MISSING',
      payload,
    });
  }

  static setContextReceived() {
    this.sendMessage({ type: 'GC/CONTEXT_RECEIVED' });
  }

  static setFetchGameStarted() {
    this.sendMessage({ type: 'GC/FETCH_GAME_STARTED' });
  }

  static setFetchGameSuccess(payload?: { gamingSessionTrackingId?: string }) {
    this.sendMessage({
      type: 'GC/FETCH_GAME_SUCCESS',
      ...(payload && { payload }),
    });
  }

  static setGeolocationSuccess(payload: GeoPassedEvent) {
    this.sendMessage({
      type: 'GC/GEOLOCATION_SUCCESS',
      payload,
    });
  }

  static setGeolocationInvalidated(payload: GeoInvalidatedEvent) {
    this.sendMessage({
      type: 'GC/GEOLOCATION_INVALIDATED',
      payload,
    });
  }

  static setGeolocationRejected(payload: GeoRejectedEvent) {
    this.sendMessage({
      type: 'GC/GEOLOCATION_REJECTED',
      payload,
    });
  }

  static setGeolocationFailure(payload: GeoFailureEvent) {
    this.sendMessage({
      type: 'GC/GEOLOCATION_FAILURE',
      payload,
    });
  }

  static setGeolocationProgress() {
    this.sendMessage({
      type: 'GC/GEOLOCATION_PROGRESS',
    });
  }

  static setUpdateGeocomplyFailure() {
    this.sendMessage({ type: 'GC/UPDATE_GEOCOMPLY_FAILURE' });
  }

  static setUpdateGeocomplySuccess() {
    this.sendMessage({ type: 'GC/UPDATE_GEOCOMPLY_SUCCESS' });
  }

  static setUpdateAuthenticationFailure() {
    this.sendMessage({ type: 'GC/UPDATE_AUTHENTICATION_FAILURE' });
  }

  static setUpdateAuthenticationSuccess() {
    this.sendMessage({ type: 'GC/UPDATE_AUTHENTICATION_SUCCESS' });
  }

  static resumeIncompleteGame(payload: string) {
    this.sendMessage({
      type: 'GC/RESUME_INCOMPLETE_GAME',
      payload,
    });
  }

  static setGameSwitch(
    payload: { rgsCode: string; provider: ProviderType },
    gameSwitchEnabled?: string | boolean,
  ) {
    if (gameSwitchEnabled !== 'on') {
      return;
    }

    this.sendMessage({
      type: 'GC/GAME_SWITCH',
      payload,
    });
  }

  static requestGameWindowUIState() {
    if (!this.isIosWrapper()) {
      this.sendMessage({ type: 'GC/REQUEST_GAME_WINDOW_UI_STATE' });
    }
  }

  static locateUser(domain: 'casino' | 'poker' = 'casino') {
    this.sendMessage({
      type: CLIENT_TO_WRAPPER_EVENT_TYPES.LOCATE_USER,
      payload: { domain },
    });
  }

  static sendDetachSessionSnapshot(payload: {
    fgpGameData: unknown;
    geocomplyToken?: string;
  }) {
    this.sendMessage({
      type: CLIENT_TO_WRAPPER_EVENT_TYPES.DETACH_SESSION_SNAPSHOT,
      payload,
    });
  }
}
